import {
  useState, useCallback, useEffect, useMemo,
} from 'react'
import axios from 'axios'

function usePromoteModel({
  itemId,
  closeModal,
  onStartCampaign,
  salesBoostPoints,
  limitTagCampaignsAndCostInflation,
  isReadOnly,
  submittingCampaign,
}) {
  const [selectedTags, setSelectedTags] = useState([])
  const [duration, setDuration] = useState(7)
  const [contentPolicyAgreed, setContentPolicyAgreed] = useState(false)
  const [tags, setTags] = useState([])
  const [tagsLoading, setTagsLoading] = useState(false)

  useEffect(() => {
    if (itemId) {
      setTagsLoading(true)
      axios.get(`/api/internal/items/${itemId}/promotion_info`)
        .then(response => {
          setTags(response.data.tags || [])
          if (isReadOnly) {
            setDuration(response.data.duration)
          }
        })
        .catch(() => {
          App.events.publish('app:notification:flash:error', 'Failed to load tags') // eslint-disable-line no-undef
          window.Sentry.captureException(new Error('Failed to load tags'))
          closeModal()
        })
        .finally(() => {
          setTagsLoading(false)
        })
    }
  }, [itemId, closeModal])

  useEffect(() => {
    if (isReadOnly && tags.length > 0) {
      const preSelectedTags = tags.filter(tag => tag.isSelected)
      setSelectedTags(preSelectedTags)
    }
  }, [isReadOnly, tags])

  const onModalClose = useCallback(() => {
    closeModal()
  }, [closeModal])

  const onTagSelect = useCallback((tagObj) => {
    setSelectedTags(prevTags => {
      const isSelected = prevTags.find(t => t.tag === tagObj.tag)
      if (isSelected) {
        return prevTags.filter(t => t.tag !== tagObj.tag)
      }

      return [...prevTags, tagObj]
    })
  }, [])

  const onLevelSelect = useCallback(levelTags => {
    setSelectedTags(prevTags => {
      const selectedLevelTags = prevTags.filter(selected => levelTags.some(levelTag => levelTag.tag === selected.tag))

      if (selectedLevelTags.length === levelTags.length) {
        return prevTags.filter(selected => !levelTags.some(levelTag => levelTag.tag === selected.tag))
      }
      const tagsToAdd = levelTags.filter(levelTag => !prevTags.some(selected => selected.tag === levelTag.tag))

      return [...prevTags, ...tagsToAdd]
    })
  }, [])

  const getLevelCheckboxState = useCallback((levelTags) => {
    const selectedLevelTags = selectedTags.filter(selected => levelTags.some(levelTag => levelTag.tag === selected.tag))

    if (selectedLevelTags.length === 0) {
      return { checked: false, isIndeterminate: false }
    } if (selectedLevelTags.length === levelTags.length) {
      return { checked: true, isIndeterminate: false }
    }
    return { checked: true, isIndeterminate: true }
  }, [selectedTags])

  const onAllTagsSelect = useCallback(() => {
    setSelectedTags(prevTags => {
      if (prevTags.length === 0 || prevTags.length !== tags.length) {
        return tags
      }

      return []
    })
  }, [tags])

  const getAllTagsCheckboxState = useCallback(() => {
    if (selectedTags.length === 0) {
      return { checked: false, isIndeterminate: false }
    }

    if (selectedTags.length !== tags.length) {
      return { checked: true, isIndeterminate: true }
    }

    return { checked: true, isIndeterminate: false }
  }, [selectedTags, tags])

  const onDurationChange = useCallback((e) => {
    setDuration(parseInt(e.target.value, 10))
  }, [])

  const calculateTotalCost = useCallback(() => {
    const baseCostPerDay = 1
    const competitionMultiplier = {
      1: 1,
      2: 2,
      3: 3,
      4: 4,
      5: 5,
    }

    return selectedTags.reduce((total, tagObj) => {
      let costPerDay

      if (limitTagCampaignsAndCostInflation) {
        const competitionLevel = tagObj.complexity || 1
        const penalty = tagObj.penalty || 0
        costPerDay = competitionLevel + penalty
      } else {
        const multiplier = competitionMultiplier[tagObj.complexity] || 1
        costPerDay = baseCostPerDay * multiplier
      }

      return total + (costPerDay * duration)
    }, 0)
  }, [selectedTags, duration, limitTagCampaignsAndCostInflation])

  const validationErrors = useMemo(() => {
    const errors = []

    if (!contentPolicyAgreed) {
      errors.push({
        type: 'content_policy',
        message: 'You need to confirm agreement with our policies',
      })
    }

    if (selectedTags.length === 0) {
      errors.push({
        type: 'no_tags_selected',
        message: 'At least one tag needs to be selected',
      })
    }

    return errors
  }, [contentPolicyAgreed, selectedTags.length])

  const canStartCampaign = useCallback(() => {
    const totalCost = calculateTotalCost()
    return selectedTags.length > 0
      && contentPolicyAgreed
      && salesBoostPoints >= totalCost
      && !submittingCampaign
  }, [selectedTags.length, contentPolicyAgreed, salesBoostPoints, calculateTotalCost, submittingCampaign])

  const onStartCampaignClick = useCallback(() => {
    if (canStartCampaign()) {
      onStartCampaign({
        selectedTags,
        duration,
        totalCost: calculateTotalCost(),
      })
    }
  }, [canStartCampaign, onStartCampaign, selectedTags, duration, calculateTotalCost])

  const toggleContentPolicyAgreement = useCallback(() => {
    setContentPolicyAgreed(prev => !prev)
  }, [])

  return {
    selectedTags,
    duration,
    contentPolicyAgreed,
    tags,
    tagsLoading,
    validationErrors,
    onModalClose,
    onTagSelect,
    onLevelSelect,
    getLevelCheckboxState,
    onAllTagsSelect,
    getAllTagsCheckboxState,
    onDurationChange,
    calculateTotalCost,
    canStartCampaign,
    onStartCampaignClick,
    toggleContentPolicyAgreement,
  }
}

export default usePromoteModel
